import { useRouter, useRoute } from 'vue-router';
import type { Field, FilterOption, SearchFilter } from '../search-panel-types';
import { SearchQuery, Filter, AggregateCount } from '../types';
import { computed, ref, watch } from 'vue';
import deepEqual from 'deep-equal';
import { getFilterString, getSearchQueryFromRoute } from '../query-formatters';
import { AgeFilter, getRangeBounds } from '../content-filters';

export const useFilters = () => {
  const router = useRouter();
  const route = useRoute();

  //The search query (q, filter, sort, …) parsed from the current route. The route is the source of truth.
  // With keep-alive, a page stays mounted after navigating away. Only follow the route while it is this page's
  // route, so e.g. selecting a filter in Library doesn't change the filters of the hidden Materials page
  const ownPath = route.path;
  const searchQuery = ref<SearchQuery>(getSearchQueryFromRoute(route.query));
  watch(
    () => route.query,
    (query) => {
      if (route.path !== ownPath) {
        return;
      }
      const next = getSearchQueryFromRoute(query);
      // Only replace it on real changes, so watchers (e.g. the search in SearchPanel) don't run for nothing
      if (!deepEqual(next, searchQuery.value)) {
        searchQuery.value = next;
      }
    },
  );
  const selectedFilters = computed<SearchFilter>(() => filtersFromQuery(searchQuery.value));

  /**
   * Converts a SearchQuery's filter to SearchFilter options, e.g. { categories: ['a', 'b'] } →
   * { categories: [{ value: 'a', … }, { value: 'b', … }] }. The label is the raw value; components translate it.
   */
  const filtersFromQuery = (query: SearchQuery): SearchFilter => {
    const filters: SearchFilter = {};

    if (!query.filter || Object.keys(query.filter).length === 0) return {};

    Object.keys(query.filter).forEach((queryKey) => {
      if (!query.filter || !query.filter[queryKey]) return;

      const queryFilter = query.filter[queryKey];
      if (Array.isArray(queryFilter)) {
        filters[queryKey] = queryFilter.map((item) => ({ value: item, label: item, count: 1 }));
      } else if (typeof queryFilter !== 'string') {
        throw new Error(`Invalid filter type ${typeof queryFilter} for ${queryKey}`);
      } else {
        filters[queryKey] = [{ value: queryFilter, label: queryFilter, count: 1 }];
      }
    });

    return filters;
  };

  /**
   * Replaces all filters in the route with `filters`, written as filter=key:value (the format getSearchQueryFromRoute
   * reads). Keys without options are dropped, and other params (q, sort, …) are kept.
   */
  const writeFilters = (filters: SearchFilter) => {
    const activeFilters: Filter = {};
    for (const filterKey in filters) {
      const filterOptions = filters[filterKey];

      if (filterOptions.length > 0) {
        activeFilters[filterKey] = filterOptions.map((option) => option.value);
      }
    }

    const filterStrings = getFilterString(activeFilters);
    const currentFilterStrings = [route.query.filter ?? []].flat();
    // Skip the navigation (and the search it triggers) when nothing changes, e.g. a component re-emitting its value
    if (deepEqual(filterStrings, currentFilterStrings)) {
      return;
    }

    router.push({ query: { ...route.query, filter: filterStrings, source: 'filter_or_sort' } });
  };

  /**
   * Updates the given filter keys in the route and keeps the other selected filters.
   */
  const filtersToQuery = (filter: SearchFilter) => {
    writeFilters({ ...selectedFilters.value, ...filter });
  };

  const removeSelectedFilterByValue = (filterKey: string, option: FilterOption) => {
    const remainingOptions = (selectedFilters.value[filterKey] ?? []).filter(
      (filterOption) => filterOption.value !== option.value,
    );
    writeFilters({ ...selectedFilters.value, [filterKey]: remainingOptions });
  };

  const removeAllFilters = () => {
    writeFilters({});
  };

  /**
   * The selected filters in API format for the search. A selected age range is kept within minAge/maxAge,
   * and without one, minAge/maxAge become the age filter.
   */
  const getFormattedFilters = (minAge?: number, maxAge?: number): Filter => {
    const filter = searchQuery.value.filter ?? {};
    let prepared: Filter = {};

    Object.keys(filter).forEach((key: string) => {
      // Handle Age Range Filter (User Selection), e.g. 'age(3, 10)'
      if (key === 'ages') {
        const ages = filter[key];
        const selectedAges = typeof ages === 'string' ? getRangeBounds(ages) : undefined;
        if (!selectedAges) {
          return;
        }
        const finalMin = minAge !== undefined ? Math.max(selectedAges.min, minAge) : selectedAges.min;
        const finalMax = maxAge !== undefined ? Math.min(selectedAges.max, maxAge) : selectedAges.max;
        if (finalMin <= finalMax) {
          prepared = { ...prepared, ...AgeFilter(finalMin, finalMax) };
        }
        return;
      }
      // Other filters, including ranges like duration:[60 TO 300], are already in API format in the route
      prepared[key] = filter[key];
    });

    // Apply Global Defaults ONLY if the user selected no 'ages' filter
    if (!prepared.ages && minAge !== undefined && maxAge !== undefined) {
      prepared = { ...prepared, ...AgeFilter(minAge, maxAge) };
    }
    return prepared;
  };

  /** The aggregation keys of all fields with aggregate: true, including sub-fields of grouped fields */
  const getAggregationKeys = (items: Field[]): string[] =>
    items.reduce((arrangedFields: string[], item: Field) => {
      if ('fields' in item) {
        return [...arrangedFields, ...getAggregationKeys(item.fields)];
      }
      if (!item.aggregate) {
        return arrangedFields;
      }
      return [item.aggregationKey, ...arrangedFields];
    }, []);

  /** The fields without hidden ones (isHidden), also within grouped fields */
  const filterVisibleFields = (fields: Field[]): Field[] => {
    return fields
      .filter((field) => !field.isHidden)
      .map((field) => ('fields' in field ? { ...field, fields: filterVisibleFields(field.fields) } : field));
  };

  /** The min and max age as aggregation counts, so the age filter shows the allowed age range */
  const getAgeFilterItems = (minAge: number, maxAge: number): AggregateCount[] => [
    { key: minAge.toString(), count: 1 },
    { key: maxAge.toString(), count: 1 },
  ];

  return {
    searchQuery,
    selectedFilters,
    getFormattedFilters,
    getAggregationKeys,
    filterVisibleFields,
    getAgeFilterItems,
    filtersFromQuery,
    filtersToQuery,
    removeSelectedFilterByValue,
    removeAllFilters,
  };
};
